// src/components/resources/tools/generators/RedditPost/RedditCommentThread.tsx

'use client'

import type { FC } from 'react'
import { ArrowBigDown, ArrowBigUp, MessageSquare } from 'lucide-react'
import { clsx } from 'clsx'
import {
  avatarInitial,
  makeComment,
  normalizeHandle,
  normalizeSubreddit,
  type ThreadComment,
} from '../CommentThread/threadModel'
import { applyVote, formatCount } from './scoring'
import type { RedditConfig } from './RedditPostGenerator'

/**
 * Default comment tree for the Reddit comment-thread mode: three levels deep
 * (comment → OP reply → follow-up) so the indent rails and nested-reply
 * editing are visible before the user touches anything. The OP reply's
 * username matches the post's default author, so the OP badge reads correctly
 * out of the box.
 */
export function buildDefaultRedditComments(): ThreadComment[] {
  return [
    makeComment({
      id: 'rd-default-1',
      username: 'pixel_perf_dev',
      text: 'We switched the whole team last quarter — the reporting exports alone save us hours every week.',
      likes: 412,
      timestamp: '5h',
      replies: [
        makeComment({
          id: 'rd-default-1-1',
          username: 'adsights',
          text: 'Great to hear! The new dashboard update ships to every workspace this week.',
          likes: 88,
          timestamp: '4h',
          byAuthor: true,
          replies: [
            makeComment({
              id: 'rd-default-1-1-1',
              username: 'pixel_perf_dev',
              text: 'Looking forward to it — the beta already fixed our attribution gaps.',
              likes: 24,
              timestamp: '3h',
            }),
          ],
        }),
      ],
    }),
    makeComment({
      id: 'rd-default-2',
      username: 'marketing_maven',
      text: 'How does it compare to building the same reports in spreadsheets?',
      likes: 57,
      timestamp: '2h',
    }),
  ]
}

export interface RedditCommentThreadProps {
  config: RedditConfig
  comments: ThreadComment[]
  /**
   * Toggle a comment's vote in the given direction. Wired to the per-comment
   * up/down arrows so voting IN the preview works exactly like the post
   * card's vote pill — ±1 on the displayed score with the orange/blue
   * highlight. The arrows report only the DIRECTION clicked; the owner
   * resolves the tri-state toggle against live state inside its functional
   * updater, because this card renders from a deferred snapshot — computing
   * "next" from the rendered `comment.vote` here let a rapid second click
   * send the same direction twice instead of clearing it (`r3564364629`).
   */
  onVote?: (commentId: string, direction: 'up' | 'down') => void
}

/**
 * Reddit comment-thread card: a compact post-context header (community byline
 * + title), then the nested comment tree with Reddit's vertical indent rails.
 * Usernames route through the shared normalize-first helpers, so `u/name` and
 * the avatar initial render identically whether the handle was typed with or
 * without a leading `@`/`u/`. Comments flagged `byAuthor` carry Reddit's blue
 * OP badge. Scores render through the same lowercase compact formatter as the
 * post card (and may be negative, as real comment scores can be).
 */
export const RedditCommentThread: FC<RedditCommentThreadProps> = ({ config, comments, onVote }) => {
  // normalizeSubreddit strips a pasted r/ prefix, so the header renders
  // "r/marketing" (never "r/r/marketing") and the community avatar initial
  // derives from the community name, not the prefix.
  const subreddit = normalizeSubreddit(config.subreddit)
  const isDark = config.theme === 'dark'
  const cardBg = isDark ? 'bg-[#1a1a1b] border-[#343536]' : 'bg-white border-gray-200'
  const titleText = isDark ? 'text-[#d7dadc]' : 'text-gray-900'
  const bodyText = isDark ? 'text-[#d7dadc]' : 'text-gray-800'
  const mutedText = isDark ? 'text-[#818384]' : 'text-gray-500'
  const railColor = isDark ? 'border-[#343536]' : 'border-gray-200'

  const renderComment = (comment: ThreadComment, depth: number): JSX.Element => {
    // normalizeHandle strips @/u/ prefixes, so a pasted "u/brand" renders
    // "u/brand" here, never "u/u/brand".
    const name = normalizeHandle(comment.username)
    const upActive = comment.vote === 'up'
    const downActive = comment.vote === 'down'
    const scoreColor = upActive ? 'text-[#ff4500]' : downActive ? 'text-[#7193ff]' : titleText
    return (
      <div key={comment.id} className={clsx(depth > 0 && clsx('ml-3 border-l-2 pl-3', railColor))}>
        <div className="flex items-center gap-2 pt-3">
          <div
            aria-hidden="true"
            className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-rose-500 text-[11px] font-bold text-white"
          >
            {avatarInitial(name)}
          </div>
          <span className={clsx('text-[12px] font-bold', titleText)}>u/{name}</span>
          {comment.byAuthor && <span className="text-[11px] font-bold text-[#0079d3]">OP</span>}
          <span className={clsx('text-[12px]', mutedText)}>· {comment.timestamp}</span>
        </div>
        <p className={clsx('mt-1 whitespace-pre-wrap pl-8 text-[14px] leading-relaxed', bodyText)}>{comment.text}</p>
        <div className={clsx('mt-1 flex items-center gap-1 pl-8 text-[12px] font-bold', mutedText)}>
          {/* Per-comment vote arrows — interactive like the post card's pill. */}
          <button
            type="button"
            onClick={() => onVote?.(comment.id, 'up')}
            aria-pressed={upActive}
            aria-label={`${upActive ? 'Remove upvote from' : 'Upvote'} comment by u/${name || 'user'}`}
            className="rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10"
          >
            <ArrowBigUp className={clsx('h-4 w-4', upActive && 'fill-[#ff4500] text-[#ff4500]')} />
          </button>
          <span className={clsx('min-w-[2ch] text-center tabular-nums', scoreColor)}>
            {formatCount(applyVote(comment.likes, comment.vote))}
          </span>
          <button
            type="button"
            onClick={() => onVote?.(comment.id, 'down')}
            aria-pressed={downActive}
            aria-label={`${downActive ? 'Remove downvote from' : 'Downvote'} comment by u/${name || 'user'}`}
            className="rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10"
          >
            <ArrowBigDown className={clsx('h-4 w-4', downActive && 'fill-[#7193ff] text-[#7193ff]')} />
          </button>
          <span className="ml-2 flex items-center gap-1">
            <MessageSquare aria-hidden="true" className="h-3.5 w-3.5" />
            Reply
          </span>
        </div>
        {comment.replies.map(reply => renderComment(reply, depth + 1))}
      </div>
    )
  }

  return (
    <article className={clsx('mx-auto w-full max-w-2xl overflow-hidden rounded-md border shadow-xl', cardBg)}>
      <div className="p-3 sm:p-4">
        {/* Compact post context */}
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-orange-500 to-rose-500 text-[11px] font-bold text-white">
            {(subreddit[0] ?? 'r').toUpperCase()}
          </div>
          <span className={clsx('text-[13px] font-bold', titleText)}>r/{subreddit}</span>
          <span className={clsx('text-[13px]', mutedText)}>·</span>
          <span className={clsx('truncate text-[12px]', mutedText)}>
            Posted by u/{normalizeHandle(config.username)}
            {' · '}
            {config.timestamp}
          </span>
        </div>
        <h2 className={clsx('mt-1.5 text-[16px] font-medium leading-snug', titleText)}>{config.title}</h2>

        {/* Comment tree with indent rails */}
        <div className={clsx('mt-2 border-t pt-1', railColor)}>
          {comments.map(comment => renderComment(comment, 0))}
        </div>
      </div>
    </article>
  )
}
